import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { School, Login } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import Orb from '../../components/Orb/Orb';
import GooeyNavHeader from '../../components/GooeyNavHeader/GooeyNavHeader';
import SplashCursor from '../../components/SplashCursor/SplashCursor';

function HomePage() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  const navItems = user ? [
    { label: 'Home', href: '#', onClick: () => navigate('/') },
    { label: 'Courses', href: '#', onClick: () => navigate('/courses') },
    { label: 'About', href: '#', onClick: () => {} },
    { label: 'Contact', href: '#', onClick: () => {} },
    { label: 'Sign Out', href: '#', onClick: () => { logout(); navigate('/'); } },
  ] : [
    { label: 'Home', href: '#', onClick: () => navigate('/') },
    { label: 'About', href: '#', onClick: () => {} },
    { label: 'Contact', href: '#', onClick: () => {} },
    { label: 'Sign In', href: '#', onClick: () => navigate('/login') },
    { label: 'Sign Up', href: '#', onClick: () => navigate('/login') },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* 主页面流体光标效果 - 仅首页显示 */}
      <SplashCursor />

      {/* Header - 与课程选择、带 sidebar 页面一致 */}
      <GooeyNavHeader
        navItems={navItems}
        initialActiveIndex={0}
      />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
      {/* Orb Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}
      >
        <Orb
          hoverIntensity={2}
          rotateOnHover
          hue={0}
          forceHoverState={false}
          backgroundColor="#000000"
        />
      </Box>

      {/* Content */}
      <Container
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          py: 8
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
            fontWeight: 700,
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          教学平台
        </Typography>

        <Typography
          variant="h5"
          component="p"
          sx={{
            mb: 6,
            color: 'text.secondary',
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}
        >
          智能化的在线学习与教学系统
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mb: 8 }}
        >
          {user ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<School />}
              onClick={() => navigate('/courses')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                }
              }}
            >
              进入课程
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<Login />}
              onClick={() => navigate('/login')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                }
              }}
            >
              登录
            </Button>
          )}
        </Stack>

        {/* Feature Cards */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px) saturate(180%)',
                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  智能学习
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI 驱动的个性化学习体验，帮助你更高效地掌握知识
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px) saturate(180%)',
                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  实时反馈
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  即时获得作业和练习的反馈，快速提升学习效果
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px) saturate(180%)',
                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  丰富资源
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  涵盖多种题型的题库和详细的课程笔记，满足不同学习需求
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
